import { useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { isInSaaSMode } from '@elementary/common-ui/utils/common';
import { useLineageGraphContext } from './LineageGraphContext';
import { getLineageNodeDetailsQuery } from './api';
import { Invocation, InvocationData, Resource, ResourceType } from './types';

type LineageNodeDetails = {
  id: string;
  description: string | null;
  latestExecution?: {
    job?: {
      orchestrator?: string | null;
      jobId?: string | null;
      jobName?: string | null;
      jobUrl?: string | null;
      jobRunId?: string | null;
      jobRunUrl?: string | null;
    } | null;
  } | null;
};

type LineageNodeInfo = {
  resource: Resource | null;
  totals?: { passed: number; warnings: number; errors: number };
  jobInvocation: Invocation;
  hasCatalogEntry: boolean;
  catalogTreeType: 'bi_assets' | 'data_assets';
};

/**
 * Resolves the lineage info-panel data for the highlighted node. In OSS it reads
 * the report (`models` / `test_results_totals` / `invocations`); in SaaS the
 * API-enriched node already carries owners/tags/db-schema-table/path/test
 * executions, so we only fetch the asset `description` + `latestExecution.job`.
 */
export const useLineageNodeInfo = (): LineageNodeInfo => {
  const { highlightedNode, reportData } = useLineageGraphContext();
  const envid = useSelectedEnv();
  const isSaaS = isInSaaSMode();

  const nodeId = highlightedNode?.parentId || highlightedNode?.id;
  const metadata = highlightedNode?.metadata;

  const { data: nodeDetails } = useGraphqlQuery<LineageNodeDetails | null>({
    query: getLineageNodeDetailsQuery,
    queryKey: ['lineage-node-details', envid, nodeId],
    variables: { id: nodeId },
    enabled: isSaaS && !!nodeId,
    select: (data: { asset: LineageNodeDetails | null }) => data.asset,
  });

  const resource = useMemo<Resource | null>(() => {
    if (!isSaaS) {
      const modelData = nodeId ? reportData?.models?.[nodeId] : undefined;
      if (modelData) {
        return {
          resourceType: modelData.artifact_type as ResourceType,
          owners: modelData.owners,
          tags: modelData.tags,
          description: modelData.description,
          meta: modelData.meta,
          databaseName: modelData.database_name,
          schemaName: modelData.schema_name,
          tableName: modelData.table_name,
          url: modelData.url,
          type: modelData.type,
          maturity: modelData.maturity,
          fqn: modelData.fqn,
        } as Resource;
      }
      if (metadata) {
        return {
          name: metadata.label,
          resourceType: ResourceType.Model,
          owners: metadata.owners ?? [],
          tags: metadata.tags ?? [],
          fqn: metadata.path ?? '',
        } as Resource;
      }
      return null;
    }

    if (!metadata) return null;

    return {
      name: metadata.label,
      resourceType: (metadata.nodeType as ResourceType) ?? ResourceType.Model,
      owners: metadata.owners ?? [],
      tags: metadata.tags ?? [],
      description: nodeDetails?.description ?? undefined,
      meta:
        metadata.platform || metadata.subType
          ? {
              ...(metadata.platform ? { platform: metadata.platform } : {}),
              ...(metadata.subType ? { sub_type: metadata.subType } : {}),
            }
          : undefined,
      databaseName: metadata.databaseName,
      schemaName: metadata.schemaName ?? '',
      tableName: metadata.tableName ?? '',
      fqn: metadata.path ?? '',
    } as Resource;
  }, [isSaaS, nodeId, reportData, metadata, nodeDetails]);

  const totals = useMemo(() => {
    if (!isSaaS) {
      return nodeId ? reportData?.test_results_totals?.[nodeId] : undefined;
    }

    const counts = { passed: 0, warnings: 0, errors: 0 };
    metadata?.testExecutions?.forEach((test) => {
      switch (test.executionStatus) {
        case 'pass':
          counts.passed += 1;
          break;
        case 'warn':
          counts.warnings += 1;
          break;
        case 'fail':
        case 'error':
          counts.errors += 1;
          break;
        default:
          break;
      }
    });
    return counts;
  }, [isSaaS, nodeId, reportData, metadata]);

  const jobInvocation = useMemo<Invocation>(() => {
    if (!isSaaS) {
      const invocationData = highlightedNode
        ? reportData?.invocations?.find(
            (item: InvocationData) =>
              item.invocation_id ===
              reportData?.resources_latest_invocation?.[highlightedNode.id],
          )
        : undefined;

      return {
        jobId: invocationData?.job_id,
        jobName: invocationData?.job_name,
        orchestrator: invocationData?.orchestrator,
        jobUrl: invocationData?.job_url,
        jobRunUrl: invocationData?.job_run_url,
        jobRunId: invocationData?.job_run_id,
      };
    }

    const job = nodeDetails?.latestExecution?.job;
    return {
      jobId: job?.jobId ?? undefined,
      jobName: job?.jobName ?? undefined,
      orchestrator: job?.orchestrator ?? undefined,
      jobUrl: job?.jobUrl ?? undefined,
      jobRunUrl: job?.jobRunUrl ?? undefined,
      jobRunId: job?.jobRunId ?? undefined,
    };
  }, [isSaaS, reportData, highlightedNode, nodeDetails]);

  const hasCatalogEntry = isSaaS
    ? !!nodeDetails
    : !!(nodeId && reportData?.models?.[nodeId]);

  const isBiAsset = isSaaS
    ? !!metadata?.platform
    : !!(nodeId && reportData?.models?.[nodeId]?.meta?.platform);

  return {
    resource,
    totals,
    jobInvocation,
    hasCatalogEntry,
    catalogTreeType: isBiAsset ? 'bi_assets' : 'data_assets',
  };
};
